import React, { memo } from 'react';
import {
  ActionButton,
  Flex,
  Text,
  Tooltip,
  TooltipTrigger
} from '@adobe/react-spectrum';
import classNames from 'classnames';
import SavedViewsIcon from 'app/assets/svgIcons/SavedViewsIcon';
import ChevronDownIcon from 'app/assets/svgIcons/ChevronDownIcon';
import sidebarStyles from 'app/components/RoadmapCard/leftSidebar.module.scss';

interface SavedViewsHeaderProps {
  isSidebarOpen: boolean;
  isExpanded: boolean;
  // Whether the label should show the blue highlight - true only while a
  // saved view/My Stuff is the active view (mirrors Roadmap/Must Wins'
  // highlight, which is likewise based on active state, not expand/collapse
  // UI state). Deliberately independent of isExpanded: collapsing via the
  // chevron must not drop the highlight while still on that view, and
  // expanding it must not force the highlight on when some other view
  // (e.g. Must Wins) is actually active.
  isActive: boolean;
  openTooltip: string | null;
  onTooltipOpenChange: (isOpen: boolean) => void;
  onTooltipMouseEnter: () => void;
  onTooltipMouseLeave: () => void;
  onLabelPress: () => void;
  onChevronPress: () => void;
}

export const SavedViewsHeader = memo(
  ({
    isSidebarOpen,
    isExpanded,
    isActive,
    openTooltip,
    onTooltipOpenChange,
    onTooltipMouseEnter,
    onTooltipMouseLeave,
    onLabelPress,
    onChevronPress
  }: SavedViewsHeaderProps) => {
    return (
      <Flex
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        UNSAFE_className={sidebarStyles['must-wins-container']}
        data-testid="saved-views-header"
      >
        <TooltipTrigger
          isOpen={openTooltip === 'savedviews'}
          onOpenChange={onTooltipOpenChange}
          placement="top"
        >
          <div
            onMouseEnter={onTooltipMouseEnter}
            onMouseLeave={onTooltipMouseLeave}
            style={{ display: 'contents' }}
          >
            <ActionButton
              isQuiet
              data-testid="left-sidebar-savedviews-button"
              onPress={onLabelPress}
              UNSAFE_className={classNames(
                sidebarStyles['left-sidebar-icon'],
                sidebarStyles['left-sidebar-button-base'],
                sidebarStyles['must-wins-label'],
                { [sidebarStyles['active']]: isActive }
              )}
            >
              <SavedViewsIcon
                width={20}
                height={20}
                style={{ width: 20, height: 20, flexShrink: 0 }}
              />
              {isSidebarOpen && (
                <Text
                  UNSAFE_className={classNames(
                    sidebarStyles['left-sidebar-icon-text'],
                    sidebarStyles['must-wins-text']
                  )}
                >
                  Saved Views
                </Text>
              )}
            </ActionButton>
          </div>
          <Tooltip>Saved Views</Tooltip>
        </TooltipTrigger>

        {isSidebarOpen && (
          <ActionButton
            isQuiet
            data-testid="left-sidebar-savedviews-dropdown-button"
            onPress={onChevronPress}
            UNSAFE_className={classNames(
              sidebarStyles['must-wins-dropdown-button'],
              isExpanded && sidebarStyles['active'],
              isExpanded && sidebarStyles['must-wins-dropdown-button-expanded']
            )}
          >
            <ChevronDownIcon
              width={20}
              height={20}
              className={sidebarStyles['must-wins-chevron']}
            />
          </ActionButton>
        )}
      </Flex>
    );
  }
);

SavedViewsHeader.displayName = 'SavedViewsHeader';
